import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { pluck } from '/imports/utils';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { SolisStation } from '/server/integrations/solis';

export interface SolisMeter extends Doc, SolisStation {
  _id: string;
  meterId: string;       // → ProductionMeter (type 'solis')
  credentialId: string;  // → SolisCredential
}
export type Model = SolisMeter;

export const SolisMeters = new Mongo.Collection<SolisMeter>('solisMeters');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: SolisMeters,
  editableFields: ['active'],
  listMethod: DeclareMethod<ListMethodArg<SolisMeter>, ListMethodResponse<SolisMeter, Refs>>('listSolisMeters'),
  getMethod: DeclareMethod<string, GetMethodResponse<SolisMeter, Refs>>('getSolisMeter'),
  createMethod: DeclareMethod<Partial<SolisMeter>, string>('createSolisMeter'),
  updateMethod: DeclareMethod<Partial<SolisMeter>, string>('updateSolisMeter'),
  deleteMethod: DeclareMethod<string>('deleteSolisMeter'),
  pathPrefix: '/solis-meters',
  asString: (document) => document.stationName || document._id,
  modelName: 'Solis',
  pluralName: 'Solis',
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { stationName: new RegExp(search, 'gi') },
        { id: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (document: Partial<SolisMeter>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, document, 'active');
    validateString(errors, document, 'meterId', { required: true });
    return errors;
  },
};

Meteor.isServer && Meteor.startup(async () => {
  await SolisMeters.createIndexAsync({ meterId: 1 });
});

export interface SolisCredential extends Doc {
  _id: string;
  orgId: string;
  apiUrl: string;    // SolisCloud API base URL (e.g. https://www.soliscloud.com:13333)
  keyId: string;
  keySecret: string; // encrypted at rest
}

export const SolisCredentials = new Mongo.Collection<SolisCredential>('solisCredentials');

export const createSolisCredential = DeclareMethod<Partial<SolisCredential>, string>('createSolisCredential');
export const updateSolisCredential = DeclareMethod<Partial<SolisCredential>>('updateSolisCredential');
export const getSolisCredentialsForOrg = DeclareMethod<string, SolisCredential[]>('getSolisCredentialsForOrg');
export const pullSolisStations = DeclareMethod<string, { numSystems: number; numCreated: number; }>('pullSolisStations');

/**
 * Validates a SolisCloud credential, requiring org, API URL, and key pair.
 * @param credential - partial credential to validate
 * @returns map of field name to validation error messages
 */
export const validateSolisCredential = (credential: Partial<SolisCredential>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, credential, 'orgId', { required: true });
  validateString(errors, credential, 'apiUrl', { required: true });
  validateString(errors, credential, 'keyId', { required: true });
  validateString(errors, credential, 'keySecret', { required: true });
  return errors;
};

/**
 * Strips a credential down to its persistable fields.
 * @param credential - partial credential to clean
 * @returns the credential with only allowed fields
 */
export const cleanSolisCredential = (credential: Partial<SolisCredential>) => pluck(credential, ['orgId', 'apiUrl', 'keyId', 'keySecret']) as SolisCredential;
